<template>
    <CollapsibleWidget>
        <template #name>Fonts</template>

        <template #controls>
            <Icon type="md-trash" @click.exact.stop="resetStyles('FontStyle')" />
        </template>

        <div class="font-style">
            <EditableSettingsControl
                v-if="fontFamilyOptions.length"
                :title="camelToUpperCase(EditableSettingProperty.FontFamily)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <FontType width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontFamily"
                        size="small"
                        clearable
                        :placeholder="placeholderValue(EditableSettingProperty.FontFamily)"
                        class="font-family font-style__control hox-select-dark"
                    >
                        <Option
                            v-for="option in fontFamilyOptions"
                            :key="option.value"
                            class="font-family__option"
                            :value="option.value"
                        >
                            {{ option.label }}
                            <span class="font-family__icon-content">
                                <span class="font-family__icon-wrapper">
                                    <span class="pretty-file-icon-html" />
                                </span>
                                <span v-if="option.postScriptName" class="font-family__icon-wrapper">
                                    <span class="pretty-file-icon-psd" />
                                </span>
                            </span>
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.FontSize)">
                <template #button>
                    <div class="editable-settings-item">
                        <LetterSize width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="fontSize"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.FontSize)"
                        :placeholder="placeholderValue(EditableSettingProperty.FontSize)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.LetterSpacing)">
                <template #button>
                    <div class="editable-settings-item">
                        <LetterSpacing width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="letterSpacing"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.LetterSpacing)"
                        :placeholder="placeholderValue(EditableSettingProperty.LetterSpacing)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.LineHeight)">
                <template #button>
                    <div class="editable-settings-item">
                        <LineHeight width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="lineHeight"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.LineHeight)"
                        :placeholder="placeholderValue(EditableSettingProperty.LineHeight)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="isMasterTemplatePSD"
                :title="camelToUpperCase(EditableSettingProperty.TextAlignment)"
            >
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <TextAlignment width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="textAlignment"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="placeholderValue(EditableSettingProperty.TextAlignment)"
                    >
                        <Option v-for="option in textAlignmentOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.FontWeight)">
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <FontWeight width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontWeight"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="fontWeightPlaceholderValue"
                    >
                        <Option v-for="option in fontWeightOptions" :key="option" :value="option">
                            {{ option }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.FontStyle)">
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <FontItalic width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontStyle"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="fontStylePlaceholderValue"
                    >
                        <Option v-for="option in fontStyleOptions" :key="option" :value="option">
                            {{ option }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl :title="camelToUpperCase(EditableSettingProperty.FontColor)">
                <template #row>
                    <div class="font-style__control--color-row">
                        <ColorPicker
                            ref="picker"
                            v-model="fontColor"
                            class="font-style__control--color-picker"
                            size="small"
                            transfer
                        />
                        <div class="font-style__control--color-value">
                            {{ fontColorDisplay }}
                        </div>
                    </div>
                </template>
            </EditableSettingsControl>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { CSSSettings } from "hoxton-types";
import rgbToHex from "hoxton-shared/utils/rgbToHex";

import LetterSize from "@/assets/text-editor/letter-size.svg";
import LetterSpacing from "@/assets/text-editor/spacing.svg";
import LineHeight from "@/assets/text-editor/leading.svg";
import FontWeight from "@/assets/text-editor/bold-text.svg";
import TextAlignment from "@/assets/text-editor/text-align.svg";
import FontItalic from "@/assets/text-editor/italics.svg";
import FontType from "@/assets/text-editor/type.svg";

import EditableSettingsControl from "@/components/Campaign/SettingsPane/EditableSettingsControl.vue";
import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import EditableSettingsNumberInput from "@/components/Campaign/SettingsPane/EditableSettingsNumberInput.vue";

import { useEditableSettings } from "@/composables/useEditableSettings";
import { useEditor } from "@/composables/useEditor";

import { camelToUpperCase } from "@/utils";
import { EditablePhotoshopSettings, EditableSettingProperty } from "@/enums/editables";

const emptyPropertyText = "Not set";
const emptySelect = "Select";
const fontWeightOptions = ["normal", "bold"];
const fontStyleOptions = ["normal", "italic"];
const textAlignmentOptions = [
    { label: "Left", value: "left" },
    { label: "Center", value: "center" },
    { label: "Right", value: "right" },
    { label: "Justify Left", value: "justifyLeft" },
    { label: "Justify Center", value: "justifyCenter" },
    { label: "Justify Right", value: "justifyRight" },
    { label: "Justify All", value: "justifyAll" }
];

const { computedStyle, boundingClientRect } = defineProps<{
    computedStyle: CSSStyleDeclaration;
    boundingClientRect: DOMRect;
}>();

const emit = defineEmits<(e: "onSettingsChange", value: CSSSettings | null) => void>();

const onSettingsChange = (settings: CSSSettings | null) => {
    emit("onSettingsChange", settings);
};

const { isMasterTemplatePSD, fonts, fontIds } = useEditor();

const {
    fontSize,
    fontColor,
    fontFamily,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    textAlignment,

    isSuffixVisible,
    getPlaceholderValue,
    getPsdPlaceholderValue,
    resetStyles
} = useEditableSettings({
    onSettingsChange,
    boundingClientRect,
    computedStyle
});

const fontColorDisplay = computed(() => {
    if (fontColor.value === undefined) {
        if (isMasterTemplatePSD.value) {
            const { rgb } = getPsdPlaceholderValue("fontColor") as {
                rgb: {
                    red?: number;
                    green?: number;
                    blue?: number;
                };
            };

            if (rgb?.green !== undefined && rgb?.red !== undefined && rgb?.blue !== undefined) {
                return rgbToHex(rgb.red, rgb.green, rgb.blue, true);
            }
        } else {
            return getPlaceholderValue("fontColor") || emptyPropertyText;
        }
    }
    return fontColor.value;
});

const fontFamilyOptions = computed(() =>
    fontIds.value.map(fontId => {
        const { _id: value, name: label, postScriptName } = fonts.value[fontId];

        return { label, value, postScriptName };
    })
);

const fontStylePlaceholderValue = computed(() => getPlaceholderValue("fontStyle") || emptySelect);

const fontWeightPlaceholderValue = computed(() => {
    const value = isMasterTemplatePSD.value ? getPsdPlaceholderValue("fontWeight") : getPlaceholderValue("fontWeight");

    switch (value) {
        case "400":
            return "normal";
        case "700":
            return "bold";
        default:
            return value || emptySelect;
    }
});

function placeholderValue(prop: keyof typeof EditablePhotoshopSettings) {
    if (isMasterTemplatePSD.value) {
        return getPsdPlaceholderValue(EditablePhotoshopSettings[prop]).toString();
    }

    return getPlaceholderValue(prop);
}
</script>

<style lang="scss">
@use "@style/variables" as *;

.font-style {
    &__control {
        &--color-value {
            position: absolute;
            width: calc(100% - 40px);
            z-index: 1;
            top: 0;
            margin-left: 40px;
            padding: 0 10px;
            border-left: 1px solid $cmp-dark-bg-color;
        }

        &--color-row {
            position: relative;
            width: 100%;
        }

        &--color-picker {
            cursor: pointer;
            position: relative;
            width: 100%;
            z-index: 2;

            .ivu-input-icon {
                display: none;
            }

            .ivu-input {
                border: transparent 0;
                background-color: inherit;
                width: 140px;

                &:hover,
                &:active {
                    border: transparent 0;
                    cursor: pointer;
                }
            }

            .ivu-input-icon-normal + .ivu-input {
                padding: 2px 6px;
                width: 40px;
                height: 33px;
            }

            .ivu-color-picker-small .ivu-color-picker-color {
                margin: 4px 6px;
            }
        }
    }

    &__color-value {
        width: 100%;
        height: 100%;
        cursor: pointer;
        background-color: green;
    }
}

.font-family {
    max-width: 186px;
    &__option {
        padding-right: 100px;
        position: relative;
    }

    &__icon-content {
        display: flex;
        padding-left: 5px;
    }

    &__icon-wrapper {
        display: inline-block;
        width: 24px;
        height: 24px;
        filter: grayscale(100%);
    }

    .ivu-select-dropdown-list {
        padding-left: 0;
    }

    .ivu-select-dropdown {
        max-width: 186px !important;
    }

    .ivu-select-item {
        padding: 7px 10px;
        display: flex;
        align-items: center;
        white-space: normal;
    }
}
</style>
